import { Instance, getParentOfType, types } from "mobx-state-tree";
import {
  SurveySettingsModel,
  createSurveySettingsModel,
  SurveySettingsDialogModel,
  createSurveySettingsDialogModel,
  FontSizeScale,
} from "@pulse/shared-components";
import { BackOfficeSurveySettings } from "@pulse/pulse-rpcs/lib/survey/backOfficeSurveySettings";
import { SurveyTheme } from "@pulse/pulse-rpcs/lib/types/surveyTheme";
import {
  BackOfficeSurveySettingsUIStore,
  createBackOfficeSurveySettingsUIStore,
} from "../store/BackOfficeSurveySettingsUIStore";
import { BackOfficeSurveySettingsStore } from "../store/BackOfficeSurveySettingsStore";
import {
  createRTEFileServiceModel,
  RTEFileServiceModel,
} from "../../projects/models/RTEFileServiceModel";
import {
  createRespondentColumnDetailsModel,
  RespondentColumnDetailsModel,
} from "../../projects/models/RespondentColumnDetailsModel";
import { DropdownItem } from "@surya-digital/leo-reactjs-material-ui";
import { RespondentColumnDetails } from "@pulse/pulse-rpcs/lib/types/respondentColumnDetails";
import { RespondentColumnName } from "@pulse/pulse-rpcs/lib/types/respondentColumnName";
import { SurveyRespondentHeaderTextType } from "@pulse/pulse-rpcs/lib/types/surveyRespondentHeaderTextType";

export enum SurveyRespondentHeaderTextTypeEnum {
  NONE = "NONE",
  CUSTOM_TEXT = "CUSTOM_TEXT",
  RESPONDENT_COLUMN_WITH_FALLBACK_TEXT = "RESPONDENT_COLUMN_WITH_FALLBACK_TEXT",
}

export const BackOfficeSurveySettingsModel = types
  .model("BackOfficeSurveySettingsModel", {
    surveySettings: SurveySettingsModel,
    surveyName: types.string,
    selectedSurveyTheme: types.enumeration(
      Object.values(SurveyTheme.SurveyTheme),
    ),
    isSubmitConfirmationRequired: types.boolean,
    submitConfirmationDialogProperties: SurveySettingsDialogModel,
    promptUnansweredQuestions: types.boolean,
    promptUnansweredQuestionsDialogProperties: SurveySettingsDialogModel,
    isRestartAllowed: types.boolean,
    restartSurveyDialogProperties: SurveySettingsDialogModel,
    isSaveDraftAllowed: types.boolean,
    saveDraftDialogProperties: SurveySettingsDialogModel,
    isInfoButtonShown: types.boolean,
    surveyInformationDialogProperties: SurveySettingsDialogModel,
    isWelcomePageShown: types.boolean,
    welcomePageText: types.string,
    thankYouPageText: types.string,
    closedSurveyErrorTextTitle: types.string,
    closedSurveyErrorTextDescription: types.string,
    surveyClosedMessageTitleText: types.string,
    surveyClosedMessageDescriptionText: types.string,
    backOfficeSurveySettingsUIStore: BackOfficeSurveySettingsUIStore,
    clientLightThemeLogoId: types.string,
    clientDarkThemeLogoId: types.string,
    infoDialogDescriptionTextRTEFileService: RTEFileServiceModel,
    welcomePageTextRTEFileService: RTEFileServiceModel,
    thankYouPageTextRTEFileService: RTEFileServiceModel,
    isSurveyRespondentHeaderTextVisible: types.boolean,
    respondentColumnForSurveyRespondentText: types.maybeNull(
      RespondentColumnDetailsModel,
    ),
    customFallbackTextForSurveyRespondentHeaderText: types.maybeNull(
      types.string,
    ),
    surveyRespondentHeaderType: types.enumeration(
      "SurveyRespondentHeaderTextTypeEnum",
      Object.values(SurveyRespondentHeaderTextTypeEnum),
    ),
  })
  .actions((store) => ({
    setSelectedSurveyRespondentHeaderType: (
      selectedSurveyRespondentHeaderType: string,
    ): void => {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.customFallbackTextForSurveyRespondentHeaderText = null;
      store.respondentColumnForSurveyRespondentText = null;
      store.surveyRespondentHeaderType =
        SurveyRespondentHeaderTextTypeEnum[
          selectedSurveyRespondentHeaderType as keyof typeof SurveyRespondentHeaderTextTypeEnum
        ];
    },
    setIsSurveyRespondentHeaderTextVisible(
      isSurveyRespondentHeaderTextVisible: boolean,
    ): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.customFallbackTextForSurveyRespondentHeaderText = null;
      store.respondentColumnForSurveyRespondentText = null;
      if (!isSurveyRespondentHeaderTextVisible) {
        store.surveyRespondentHeaderType =
          SurveyRespondentHeaderTextTypeEnum.NONE;
      } else {
        store.surveyRespondentHeaderType =
          SurveyRespondentHeaderTextTypeEnum.RESPONDENT_COLUMN_WITH_FALLBACK_TEXT;
      }
      store.isSurveyRespondentHeaderTextVisible =
        isSurveyRespondentHeaderTextVisible;
    },
    setCustomFallbackTextForSurveyRespondentHeaderText(
      customTextForSurveyRespondentHeaderText: string,
    ): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.customFallbackTextForSurveyRespondentHeaderText =
        customTextForSurveyRespondentHeaderText;
    },
    setSelectedRespondentColumn(selectedColumn: DropdownItem): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.respondentColumnForSurveyRespondentText =
        createRespondentColumnDetailsModel(
          new RespondentColumnDetails(
            new RespondentColumnName(selectedColumn.name),
            Number(selectedColumn.value),
          ),
        );
    },
    setLightThemeLogoId(lightThemeLogoId: string): void {
      store.clientLightThemeLogoId = lightThemeLogoId;
    },
    setDarkThemeLogoId(darkThemeLogoId: string): void {
      store.clientDarkThemeLogoId = darkThemeLogoId;
    },
    setSelectedLanguageCode(selectedLanguageCode: string): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setSelectedLanguageCode(selectedLanguageCode);
    },
    setIsLanguageSelectionOptionPersistent(
      isLanguageSelectionOptionPersistent: boolean,
    ): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setIsLanguageSelectionOptionPersistent(
        isLanguageSelectionOptionPersistent,
      );
    },
    setIsLanguageSelectionDisplayedOnStart(
      isLanguageSelectionDisplayedOnStart: boolean,
    ): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setIsLanguageSelectionDisplayedOnStart(
        isLanguageSelectionDisplayedOnStart,
      );
    },
    setIsAutoProgressEnabled(isAutoProgressEnabled: boolean): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setIsAutoProgressEnabled(isAutoProgressEnabled);
    },
    setIsProgressPercentageShown(isProgressPercentageShown: boolean): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setIsProgressPercentageShown(
        isProgressPercentageShown,
      );
    },
    setIsIpTracked(doNotTrackIp: boolean): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setIsIpTracked(doNotTrackIp);
    },
    setIsRespondentConsentRequired(isRespondentConsentRequired: boolean): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setIsRespondentConsentRequired(
        isRespondentConsentRequired,
      );
    },
    setSelectedFont(font: string): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setSelectedFont(font);
    },
    setSelectedFontSize(fontSizeScale: FontSizeScale): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setSelectedFontSize(fontSizeScale);
    },
    setStartButtonText(startButtonText: string): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setStartButtonText(startButtonText);
    },
    setGenericErrorMessageTitleText(
      genericErrorMessageTitleText: string,
    ): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setGenericErrorMessageTitleText(
        genericErrorMessageTitleText,
      );
    },
    setGenericErrorMessageDescriptionText(
      genericErrorMessageDescriptionText: string,
    ): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setGenericErrorMessageDescriptionText(
        genericErrorMessageDescriptionText,
      );
    },
    setContinueButtonText(continueButtonText: string): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setContinueButtonText(continueButtonText);
    },
    setNextButtonText(nextButtonText: string): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setNextButtonText(nextButtonText);
    },
    setBackButtonText(backButtonText: string): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveySettings.setBackButtonText(backButtonText);
    },
    setSurveyName(surveyName: string): void {
      getParentOfType(store, BackOfficeSurveySettingsStore).clearRPCError();
      store.surveyName = surveyName;
    },
    setSelectedSurveyTheme(surveyTheme: SurveyTheme.SurveyTheme): void {
      store.selectedSurveyTheme = surveyTheme;
    },
    setIsSubmitConfirmationRequired(
      isSubmitConfirmationRequired: boolean,
    ): void {
      store.isSubmitConfirmationRequired = isSubmitConfirmationRequired;
    },
    setPromptUnansweredQuestions(promptUnansweredQuestions: boolean): void {
      store.promptUnansweredQuestions = promptUnansweredQuestions;
    },
    setIsRestartAllowed(isRestartAllowed: boolean): void {
      store.isRestartAllowed = isRestartAllowed;
    },
    setIsSaveDraftAllowed(isSaveDraftAllowed: boolean): void {
      store.isSaveDraftAllowed = isSaveDraftAllowed;
    },
    setIsInfoButtonShown(isInfoButtonShown: boolean): void {
      store.isInfoButtonShown = isInfoButtonShown;
    },
    setSurveyClosedMessageTitleText(
      surveyClosedMessageTitleText: string,
    ): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveyClosedMessageTitleText = surveyClosedMessageTitleText;
    },
    setSurveyClosedMessageDescriptionText(
      surveyClosedMessageDescriptionText: string,
    ): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.surveyClosedMessageDescriptionText =
        surveyClosedMessageDescriptionText;
    },
    setIsWelcomePageShown(isWelcomePageShown: boolean): void {
      store.isWelcomePageShown = isWelcomePageShown;
    },
    setWelcomePageText(welcomePageText: string): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.welcomePageText = welcomePageText;
    },
    setThankYouPageText(thankYouPageText: string): void {
      store.backOfficeSurveySettingsUIStore.clearError();
      store.thankYouPageText = thankYouPageText;
    },
    setIsRatingScaleOptionVisible(isRatingScaleOptionVisible: boolean): void {
      store.surveySettings.isRatingScaleOptionVisible =
        isRatingScaleOptionVisible;
    },
  }));

export const createBackOfficeSurveySettingsModel = (
  backOfficeSurveySettings: BackOfficeSurveySettings,
  respondentColumns: Instance<typeof RespondentColumnDetailsModel>[],
): Instance<typeof BackOfficeSurveySettingsModel> => {
  let isSurveyRespondentHeaderTextVisible = false;
  let customFallbackTextForSurveyRespondentHeaderText: string | null = null;
  let respondentColumnForSurveyRespondentText: Instance<
    typeof RespondentColumnDetailsModel
  > | null = null;
  let surveyRespondentHeaderType = SurveyRespondentHeaderTextTypeEnum.NONE;
  if (
    backOfficeSurveySettings.surveyRespondentHeaderType instanceof
    SurveyRespondentHeaderTextType.None
  ) {
    surveyRespondentHeaderType = SurveyRespondentHeaderTextTypeEnum.NONE;
    isSurveyRespondentHeaderTextVisible = false;
  } else if (
    backOfficeSurveySettings.surveyRespondentHeaderType instanceof
    SurveyRespondentHeaderTextType.CustomText
  ) {
    surveyRespondentHeaderType = SurveyRespondentHeaderTextTypeEnum.CUSTOM_TEXT;
    customFallbackTextForSurveyRespondentHeaderText =
      backOfficeSurveySettings.surveyRespondentHeaderType.customText;
    isSurveyRespondentHeaderTextVisible = true;
  } else if (
    backOfficeSurveySettings.surveyRespondentHeaderType instanceof
    SurveyRespondentHeaderTextType.RespondentColumnWithFallbackText
  ) {
    surveyRespondentHeaderType =
      SurveyRespondentHeaderTextTypeEnum.RESPONDENT_COLUMN_WITH_FALLBACK_TEXT;
    customFallbackTextForSurveyRespondentHeaderText =
      backOfficeSurveySettings.surveyRespondentHeaderType.fallbackText;
    isSurveyRespondentHeaderTextVisible = true;
    const respondentColumnName =
      backOfficeSurveySettings.surveyRespondentHeaderType.respondentColumnName;
    const respondentColumnDetails =
      respondentColumns.find(
        (respondentColumn) => respondentColumn.name === respondentColumnName,
      ) ?? null;
    if (respondentColumnDetails) {
      respondentColumnForSurveyRespondentText =
        createRespondentColumnDetailsModel(
          new RespondentColumnDetails(
            new RespondentColumnName(respondentColumnDetails.name),
            respondentColumnDetails.index,
          ),
        );
    }
  }
  return BackOfficeSurveySettingsModel.create({
    surveySettings: createSurveySettingsModel(
      backOfficeSurveySettings.surveySettings,
    ),
    backOfficeSurveySettingsUIStore: createBackOfficeSurveySettingsUIStore(),
    isInfoButtonShown: backOfficeSurveySettings.isInfoButtonShown,
    isRestartAllowed: backOfficeSurveySettings.isRestartAllowed,
    isSaveDraftAllowed: backOfficeSurveySettings.isSaveDraftAllowed,
    isSubmitConfirmationRequired:
      backOfficeSurveySettings.isSubmitConfirmationRequired,
    promptUnansweredQuestions:
      backOfficeSurveySettings.promptUnansweredQuestions,
    isWelcomePageShown: backOfficeSurveySettings.isWelcomePageShown,
    closedSurveyErrorTextDescription:
      backOfficeSurveySettings.closedSurveyErrorTextDescription.text,
    closedSurveyErrorTextTitle:
      backOfficeSurveySettings.closedSurveyErrorTextTitle.text,
    restartSurveyDialogProperties: createSurveySettingsDialogModel(
      backOfficeSurveySettings.restartSurveyDialogProperties,
    ),
    saveDraftDialogProperties: createSurveySettingsDialogModel(
      backOfficeSurveySettings.saveDraftDialogProperties,
    ),
    selectedSurveyTheme: backOfficeSurveySettings.selectedSurveyTheme,
    submitConfirmationDialogProperties: createSurveySettingsDialogModel(
      backOfficeSurveySettings.submitConfirmationDialogProperties,
    ),
    promptUnansweredQuestionsDialogProperties: createSurveySettingsDialogModel(
      backOfficeSurveySettings.promptUnansweredQuestionsDialogProperties,
    ),
    surveyClosedMessageDescriptionText:
      backOfficeSurveySettings.closedSurveyErrorTextDescription.text,
    surveyClosedMessageTitleText:
      backOfficeSurveySettings.closedSurveyErrorTextTitle.text,
    surveyInformationDialogProperties: createSurveySettingsDialogModel(
      backOfficeSurveySettings.surveyInformationDialogProperties,
    ),
    surveyName: backOfficeSurveySettings.surveyName.name,
    thankYouPageText: backOfficeSurveySettings.thankYouPageText.text,
    welcomePageText: backOfficeSurveySettings.welcomePageText.text,
    clientDarkThemeLogoId:
      backOfficeSurveySettings.clientDarkThemeLogoDocumentId.uuid,
    clientLightThemeLogoId:
      backOfficeSurveySettings.clientLightThemeLogoDocumentId.uuid,
    infoDialogDescriptionTextRTEFileService: createRTEFileServiceModel(),
    thankYouPageTextRTEFileService: createRTEFileServiceModel(),
    welcomePageTextRTEFileService: createRTEFileServiceModel(),
    isSurveyRespondentHeaderTextVisible,
    customFallbackTextForSurveyRespondentHeaderText,
    respondentColumnForSurveyRespondentText,
    surveyRespondentHeaderType,
  });
};
